import { atualUfDefinition } from "@/helpers/utils";

const CURRENT_CYCLE = "2026";

export function detectCycle(databaseName) {
  const match = String(databaseName || "").match(/20\d{2}/);
  return match ? match[0] : CURRENT_CYCLE;
}

export function detectSystem(storeState = {}) {
  if (storeState.isSala || storeState.isParceiro) {
    return {
      sistema: storeState.isParceiro
        ? "Selo Sebrae - Parceiros"
        : "Selo Sebrae de Referencia em Atendimento",
      sistemaTipo: storeState.isParceiro ? "selo_parceiros" : "selo",
    };
  }

  if (storeState.isCE) {
    return {
      sistema: "Cidade Empreendedora",
      sistemaTipo: "cidade_empreendedora",
    };
  }

  return {
    sistema: storeState.title || "Sistema",
    sistemaTipo: "outro",
  };
}

export function isSupportEnabled(storeState = {}, publicAvailability = null) {
  const ambienteComSuporte = !!(
    storeState.isSala ||
    storeState.isParceiro ||
    storeState.isCE
  );

  if (!ambienteComSuporte) {
    return false;
  }

  const config = storeState.config || getJson("config") || {};
  const groupsConfig = storeState.groupsConfig || getJson("groupsConfig") || {};
  const configValue =
    !storeState.isAuthenticated && publicAvailability !== null
      ? publicAvailability
      : hasOwn(config, "acionarSuporte")
      ? config.acionarSuporte
      : publicAvailability;
  const configHabilitada = isFlagEnabled(configValue);

  if (!configHabilitada) {
    return false;
  }

  if (!storeState.isAuthenticated) {
    return true;
  }

  if (storeState.isFullAdmin) {
    return true;
  }

  return isFlagEnabled(groupsConfig.acionarSuporte);
}

export function buildSupportContext(vm, origem) {
  const state = (vm.$store && vm.$store.state) || {};
  const user = state.user || getJson("user") || {};
  const grupo = user.grupo || {};
  const route = vm.$route || {};
  const databaseName =
    (atualUfDefinition && atualUfDefinition.banco) || state.urlTag || "";
  const system = detectSystem(state);
  const screen = window.screen || {};
  const logado = !!state.isAuthenticated;

  return {
    origem,
    sistema: system.sistema,
    sistemaTipo: system.sistemaTipo,
    ciclo: detectCycle(databaseName),
    uf: state.uf || (atualUfDefinition && atualUfDefinition.uf) || "",
    municipio: logado ? state.city || localStorage.getItem("city") || "" : "",
    municipioId: logado
      ? toNumberOrNull(state.cityId || localStorage.getItem("cityId"))
      : null,
    unidade: logado ? state.city || localStorage.getItem("city") || "" : "",
    perfil: logado ? grupo.nome || "" : "",
    usuarioNome: logado ? user.nome || "" : "",
    usuarioEmail: logado ? user.email || "" : "",
    usuarioId: logado ? toNumberOrNull(user.usuarioId) : null,
    logado,
    urlAtual: window.location.href,
    paginaAtual: getPageName(route),
    rotaNome: route.name || "",
    banco: databaseName,
    ambiente: (atualUfDefinition && atualUfDefinition.ambient) || "",
    urlTag: (atualUfDefinition && atualUfDefinition.urlTag) || "",
    navegador: window.navigator.userAgent,
    sistemaOperacional: window.navigator.platform,
    tamanhoTela: `${window.innerWidth}x${window.innerHeight} / ${screen.width || ""}x${screen.height || ""}`,
    dataHoraCliente: new Date().toISOString(),
  };
}

export function getEnvironmentText(context) {
  const sistemaTipo = context.sistemaTipo || "";
  const isSelo = sistemaTipo.indexOf("selo") >= 0;
  const isCidade = sistemaTipo === "cidade_empreendedora";
  const showAmbiente = isNonProductionEnvironment(context.ambiente);

  if (!context.logado && isCidade) {
    const lines = [
      ["Sistema", context.sistema],
      ["Ciclo", context.ciclo],
      ["UF", context.uf],
    ];

    if (showAmbiente) {
      lines.push(["Ambiente", context.ambiente, true]);
    }

    return lines.filter(([, value]) => !!value);
  }

  if (!context.logado && isSelo) {
    const lines = [
      ["Sistema", context.sistema],
      ["Tipo de acesso", getAccessType(context)],
      ["Ciclo", context.ciclo],
      ["UF", context.uf],
    ];

    if (showAmbiente) {
      lines.push(["Ambiente", context.ambiente, true]);
    }

    return lines.filter(([, value]) => !!value);
  }

  const lines = [
    ["Sistema", context.sistema],
    ["Tipo de acesso", getAccessType(context)],
    ["Ciclo", context.ciclo],
    ["UF", context.uf],
    ["Municipio", context.municipio],
    ["Unidade", context.unidade],
    ["Perfil", context.perfil],
    ["Pagina atual", context.paginaAtual],
  ];

  if (showAmbiente) {
    lines.push(["Ambiente", context.ambiente, true]);
  }

  return lines.filter(([, value]) => !!value);
}

function getAccessType(context) {
  if (!context || !context.sistemaTipo) return "";
  if (context.sistemaTipo === "selo_parceiros") return "Parceiros";
  if (context.sistemaTipo === "selo") return "Sala do Empreendedor";
  if (context.sistemaTipo === "cidade_empreendedora") {
    return "Cidade Empreendedora";
  }
  return "";
}

export function getPlaceholder(context) {
  if ((context.sistemaTipo || "").indexOf("selo") >= 0) {
    return "Ex.: nao consigo enviar evidencia, meu relatorio nao aparece, nao consigo recuperar senha, preciso trocar o e-mail cadastrado.";
  }

  return "Ex.: nao consigo acessar o SSDT, preciso cadastrar uma solucao, uma evidencia nao aparece, nao consigo visualizar uma regiao.";
}

export function getContextExpectedPlaceholder(context) {
  if (context.sistemaTipo === "cidade_empreendedora") {
    return "Informe UF, municipio ou ambiente esperado, se souber.";
  }

  return "Informe UF, municipio, sala, parceiro, entidade ou ambiente esperado, se souber.";
}

export function saveSupportContextSnapshot(vm, origem) {
  const context = buildSupportContext(vm, origem);
  const key = `suporte-contexto-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  localStorage.setItem(
    key,
    JSON.stringify({
      origem,
      contexto: context,
      criadoEm: new Date().toISOString(),
    })
  );

  return { key, context };
}

export function readSupportContextSnapshot(key) {
  if (!key) {
    return null;
  }

  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
}

function isNonProductionEnvironment(ambiente) {
  const value = String(ambiente || "").trim().toLowerCase();
  return !!value && !["prod", "prd", "producao", "production"].includes(value);
}

function getPageName(route) {
  if (route.meta && route.meta.title) return route.meta.title;
  if (route.name) return route.name;
  return route.path || "";
}

function getJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function isFlagEnabled(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function toNumberOrNull(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}
